import { CONFIGURACION_EMISOR_PATH } from "@/modules/config/configuracionRoutes";
import { CARD_CATEGORIES, CARD_STATUS } from "@/modules/cardClassificationRegistry";

/** Rutas de hubs secundarios (agrupadores). */
export const DESIGNER_SECONDARY_HUB_PATHS = {
  /** Hub UX/Roles 1A — compradores, licencias, solicitudes y plataforma SaaS. */
  COMPRADORES_LICENCIAS: "/disenador/compradores-licencias",
  LICENSES: "/disenador/licencias",
  /** Legacy — redirect a `/platform` (Fase 1 auditoría). */
  MODULES_LEGACY: "/disenador/modulos-comprador",
  /** Legacy — redirect a `/platform` (Fase 1 auditoría). */
  PLATFORM_LEGACY: "/disenador/centro-bnt",
  /** Canónico Centro de Licencias BNT (SaaS). */
  PLATFORM: "/platform",
  TOOLS: "/disenador/herramientas",
};

/**
 * Tarjetas principales de `/disenador/inicio` — 3 entradas (UX/Roles 1A).
 * @param {{ isPlatformDesigner?: boolean }} opts
 */
export function getDesignerHubPrimaryCards(opts = {}) {
  const isPlatformDesigner = Boolean(opts.isPlatformDesigner);

  const cards = [
    {
      id: "buyers_licenses",
      path: DESIGNER_SECONDARY_HUB_PATHS.COMPRADORES_LICENCIAS,
      label: "Compradores y Licencias",
      description:
        "Compradores/despachos BNT, licencias, módulos, cartera, solicitudes, prospectos y vista comprador. La licencia pertenece al comprador, no al usuario ni al cliente operativo.",
      iconKey: "catalog",
      testid: "hub-card-buyers-licenses",
      category: CARD_CATEGORIES.BUYER_COMMERCIAL,
      status: CARD_STATUS.REAL,
    },
    {
      id: "bnt_admin_users",
      path: "/usuarios-distribuidor",
      label: "Usuarios Administradores BNT",
      description:
        "Equipo interno del proveedor BNT: diseñador, administrador, soporte, comercial y auditoría. No son compradores SaaS ni auxiliares de un despacho.",
      iconKey: "users",
      testid: "hub-card-bnt-admin-users",
      category: CARD_CATEGORIES.DISTRIBUTOR_TEAM,
      status: CARD_STATUS.REAL,
    },
  ];

  if (isPlatformDesigner) {
    cards.push({
      id: "technical_tools",
      path: DESIGNER_SECONDARY_HUB_PATHS.TOOLS,
      label: "Herramientas Técnicas",
      description:
        "Diagnóstico, respaldos, mantenimiento y soporte técnico. Separado del área comercial y de compradores.",
      iconKey: "wrench",
      testid: "hub-card-technical-group",
      category: CARD_CATEGORIES.TECHNICAL_TOOLS,
      status: CARD_STATUS.REAL,
    });
  }

  return cards;
}

/** IDs de tarjetas principales (tests). */
export function getDesignerHubPrimaryCardIds(isPlatformDesigner = false) {
  return getDesignerHubPrimaryCards({ isPlatformDesigner }).map((c) => c.id);
}

/** Título de la sección transitoria del subhub (UX/Roles 1D.1). */
export const DESIGNER_CL_HUB_OPERATIONAL_SECTION_TITLE =
  "Operación actual mientras se crea la ficha única";

/**
 * Accesos transitorios — emisión, cartera y solicitudes hasta existir ficha única (UX/Roles 1D.1).
 * @param {{ isPlatformDesigner?: boolean }} opts
 */
export function getDesignerCompradoresLicenciasHubOperationalLinks(opts = {}) {
  const isPlatformDesigner = Boolean(opts.isPlatformDesigner);

  const links = [
    {
      id: "emisor",
      label: "Gestor de licencias",
      path: CONFIGURACION_EMISOR_PATH,
      description: "Emitir token, importar solicitud y generar PDF de activación.",
      testid: "hub-cl-link-emisor-transitorio",
    },
    {
      id: "cartera",
      label: "Cartera de licencias",
      path: "/cartera-licencias",
      description: "Consultar licencias emitidas, vigencias, cobros e historial.",
      testid: "hub-cl-link-cartera-transitorio",
    },
    {
      id: "buyer_requests",
      label: "Solicitudes comprador",
      path: "/disenador/solicitudes-compradores",
      description: "Revisar seguimiento comercial legacy de cuentas trial o comprador.",
      testid: "hub-cl-link-solicitudes-transitorio",
    },
  ];

  if (isPlatformDesigner) {
    links.push({
      id: "platform_centro",
      label: "Centro SaaS BNT",
      path: "/platform",
      description:
        "Organizaciones, solicitudes legales, licencias y módulos del modelo SaaS BNT.",
      testid: "hub-cl-link-platform-centro-transitorio",
    });
  }

  return links;
}

/**
 * Secciones principales del subhub — entrada única por comprador (UX/Roles 1D / 1D.1).
 * @param {{ isPlatformDesigner?: boolean }} opts — reservado; mismo bloque principal para PD y distribuidor.
 */
export function getDesignerCompradoresLicenciasHubSections(_opts = {}) {
  return [
    {
      id: "buyers_despachos",
      title: "Compradores / Despachos",
      description:
        "Entrada principal para registrar o seleccionar compradores del sistema. Desde la futura ficha del comprador " +
        "se administrarán licencia, módulos, vigencia, límites, solicitudes, legal, auxiliares y clientes operativos.",
      testid: "hub-cl-section-buyers",
      links: [
        {
          id: "catalogo",
          label: "Abrir catálogo de compradores",
          path: "/catalogo-compradores",
          testid: "hub-cl-link-catalogo",
        },
      ],
    },
  ];
}

/** IDs de secciones principales del subhub (tests). */
export function getDesignerCompradoresLicenciasHubSectionIds(isPlatformDesigner = false) {
  return getDesignerCompradoresLicenciasHubSections({ isPlatformDesigner }).map((s) => s.id);
}

/** @deprecated UX/Roles 1A.1 — use getDesignerCompradoresLicenciasHubSections */
export function getDesignerCompradoresLicenciasHubCards(opts = {}) {
  return getDesignerCompradoresLicenciasHubSections(opts).flatMap((section) =>
    (section.links || []).map((link) => ({
      id: link.id,
      path: link.path,
      label: link.label,
      action: link.action,
      testid: link.testid,
    })),
  );
}

/** Subtarjetas — Licencias distribuidor y cartera (legacy subhub /disenador/licencias). */
export function getDesignerLicenseHubCards() {
  return [
    {
      path: CONFIGURACION_EMISOR_PATH,
      label: "Gestor de licencias",
      description:
        "Emisión de tokens, registro, renovación, importación PDF y suspensión comercial.",
      iconKey: "settings",
      testid: "hub-card-license-manager",
      category: CARD_CATEGORIES.LICENSE_ADMIN,
      status: CARD_STATUS.REAL,
    },
    {
      path: "/cartera-licencias",
      label: "Cartera de licencias",
      description:
        "Historial de tokens emitidos, vigencias, equipos por contrato y estado por fila.",
      iconKey: "briefcase",
      testid: "hub-card-license-registry",
      category: CARD_CATEGORIES.LICENSE_ADMIN,
      status: CARD_STATUS.REAL,
    },
  ];
}

/** @deprecated Fase 1 — subhub eliminado; rutas legacy redirigen a `/platform`. */
export function getDesignerModulesHubCards() {
  return [];
}

/** @deprecated Fase 1 — subhub eliminado; `/disenador/centro-bnt` redirige a `/platform`. */
export function getDesignerPlatformHubCards() {
  return [];
}

/** @deprecated Use getDesignerHubPrimaryCards — compat tests legacy. */
export function getDesignerCommercialSections(opts = {}) {
  return getDesignerHubPrimaryCards(opts).map((card) => ({
    id: card.id,
    title: card.label,
    intro: card.description,
    badge: null,
    testId: `designer-hub-section-${card.id.replace(/_/g, "-")}`,
    cards: [card],
  }));
}

/** @deprecated Use getDesignerHubPrimaryCardIds */
export function getDesignerCommercialSectionIds(isPlatformDesigner = false) {
  return getDesignerHubPrimaryCardIds(isPlatformDesigner);
}
